import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { doGetClasses } from '../../../store/class.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import qs from 'qs';
import _ from 'lodash';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { Table, TableColumnsType } from 'antd';

import DownloadButton from '../../DownloadButton';
import Pagination from '../../Pagination';
import config from '../../../lib/config';
import { warningVariant, errorVariant } from '../../../lib/barVariant';
import Loader from '../../Loader';
import { IClass } from '../../../models/campusClasses.model';
import exportTable from '../../../lib/exportTable';
import styled from 'styled-components';
import { getCommonCodeLabel } from '../../../lib/store';
import i18n from '../../../lib/i18n';

const Block = styled.div`
  .closed {
    background: gainsboro;
  }
`;

const Scroll = styled.div`
  height: calc(100vh - 400px);
  overflow-y: auto;
  text-align: center;
`;

interface IProps {
  setTab: (tab: string) => void;
  refreshTable: boolean;
}

const PointTable: FC<IProps> = ({ setTab, refreshTable }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { isLoading } = useSelector((state: RootState) => state.class);

  const [data, setData] = useState<IClass[]>([]);
  const [page, setPage] = useState(1);
  const [params, setParams] = useState({});
  const [classCode, setClassCode] = useState('');
  const [isFirstRun, setIsFirstRun] = useState(true);

  useEffect(() => {
    if (query.campus && query.semester) {
      const _classCode = String(query.classCode);
      if (_classCode !== classCode) fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    query.campus,
    query.semester,
    query.keyword,
    query.includeClosed,
    query.page,
  ]);

  useEffect(() => {
    if (isFirstRun) {
      setIsFirstRun(false);
      return;
    }
    fetchData();
    setClassCode(String(query.classCode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTable]);

  const fetchData = async () => {
    // get data
    const res = await getClasses(query.campus, query.semester);

    // data filtering
    let _data: IClass[] = _.cloneDeep(res);
    const _params: any = {
      page: 1,
      campus: query.campus,
      semester: query.semester,
      semesterDiv: query.semesterDiv,
      learningYearCode: query.learningYearCode,
    };

    if (query.keyword) {
      _params.keyword = String(query.keyword).toLowerCase();
      _data = _data.filter((item: IClass) =>
        item.className?.toLowerCase().includes(_params.keyword),
      );
    }
    if (!query.includeClosed) {
      _data = _data.filter((item: IClass) => item.classStatus === 'OP');
    } else {
      _params.includeClosed = true;
    }
    if (query.page) {
      _params.page = Number(query.page);
      setPage(_params.page);
    }

    setParams(_params);
    setData(_data);
  };

  // 학급 목록 조회
  const getClasses = async (
    campusCode: any,
    semesterCode: any,
  ): Promise<IClass[]> => {
    const res = await dispatch(doGetClasses(campusCode, semesterCode));

    switch (res?.status) {
      case 200:
        return res?.data?.data;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(
          res?.data?.data || t('common.message.error'),
          errorVariant,
        );
    }

    return [];
  };

  // 학급 선택 시 쿼리 반영
  const refreshQuery = (classCode: string) => {
    const param: any = { page, classCode };

    if (query.campus) param.campus = query.campus;
    if (query.semester) param.semester = query.semester;
    if (query.keyword) param.keyword = query.keyword;

    param.semesterDiv = query.semesterDiv;
    param.learningYearCode = query.learningYearCode;

    console.log(qs.stringify(param));

    // history.push(`/class/class?${qs.stringify(param)}`);
    history.push(`/program/point?${qs.stringify(param)}`);
  };

  const onExportTable = () => {
    const currentDate = moment(new Date());
    exportTable(
      columns,
      data,
      `${t('program.point.title1')}_(${currentDate.format('YYYYMMDD')})`,
      `${t('program.point.title1')}`,
      [15, 25],
    );
  };

  const columns: TableColumnsType<any> = [
    {
      title: t('program.point.column1'),
      dataIndex: 'className',
      key: 'className',
      sorter: (a, b) => a.courseDetailName?.localeCompare(b.courseDetailName),
      align: 'center',
    },
    {
      title: t('program.point.column2'),
      dataIndex: 'courseDetailName',
      key: 'courseDetailName',
      sorter: (a, b) => a.className?.localeCompare(b.className),
      align: 'center',
      render: (value, record) =>
        record.classStatus === 'CL'
          ? `${value} (${t('program.point.column9')})`
          : value,
    },
    {
      title: t('program.semester.column33'),
      dataIndex: 'semesterType',
      key: 'semesterType',
      sorter: (a, b) => a.semesterType?.localeCompare(b.semesterType),
      align: 'center',
      render: (value: string) => getCommonCodeLabel('PR0002', value, i18n.language),
    },
  ];

  return (
    <Block>
      {isLoading ? <Loader /> : null}
      <div>
        <div className="comm-bx__between">
          <div className="comm-sch_result">
            {t('common.text.count')}: <b>{data?.length || 0}</b>
            {t('common.text.unit')}
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={data || undefined}
            pagination={{
              current: 1,
              pageSize: data.length,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  setClassCode(record.classCode);
                  refreshQuery(record.classCode);
                  // alert(record.classCode);
                },
              };
            }}
            rowClassName={(record: any) =>
              record.classCode === classCode
                ? 'chosen'
                : record.classStatus === 'CL'
                ? 'closed'
                : 'pointer'
            }
            rowKey="classCode"
          />
        </div>
        <div className="comm-bx__space">
          <DownloadButton onClick={onExportTable} />
        </div>
        {/*
        <div className="card-footer clearfix flex">
           <div className="w-100">
            <Pagination
              count={data?.length || 0}
              page={page}
              param={params}
              path="/program/point"
              small
            />
          </div> 
        </div>
        */}
      </div>
    </Block>
  );
};

export default PointTable;
